import { Text } from '@mantine/core';
import { neutralColors } from '@styles/design-system';
import { type FC } from 'react';

import styles from './NudgeStrip.module.css';

export interface Nudge {
    id: string;
    /** What is waiting, in the fewest words that still name it. */
    title: string;
    /** Why it matters, or what to do — one line. */
    detail: string;
    tone: 'warning' | 'critical';
}

/**
 * What is sitting unattended.
 *
 * Counts alone do not prompt anyone — a site that can see "12 blocked" on the stock card every
 * morning stops registering it. These say how long, because the number that changes behaviour
 * is the age, not the quantity: a batch nobody has ruled on for nine days is a decision somebody
 * has quietly stopped making.
 *
 * Rendered only when there is something to say. An empty strip is a permanent fixture that
 * teaches the eye to skip that part of the screen, which costs the nudge its only job.
 */
const NudgeStrip: FC<{ nudges: Nudge[] }> = ({ nudges }) => {
    if (nudges.length === 0) return null;

    return (
        <div className={styles.strip}>
            {nudges.map((nudge) => (
                <div key={nudge.id} className={`${styles.nudge} ${styles[nudge.tone]}`}>
                    <span className={styles.dot} />
                    <div className={styles.body}>
                        <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                            {nudge.title}
                        </Text>
                        <Text fz={12} c={neutralColors.textMuted}>
                            {nudge.detail}
                        </Text>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default NudgeStrip;
